import type { TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { TranslatePipe } from '@core/i18n-legacy';
import { Proficiency, Skill, SkillLevel, SkillMapping } from '@core/tellent-skills-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  DropdownWidth,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';

import { ProficiencyPanelComponent } from '../proficiency-panel/proficiency-panel.component';

@Component({
  selector: 'tlc-add-skills-dropdown',
  templateUrl: './add-skills-dropdown.component.html',
  styleUrls: ['./add-skills-dropdown.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    IconButtonComponent,
    IconComponent,
    InputComponent,
    OverlayOriginDirective,
    ProficiencyPanelComponent,
    TranslatePipe,
  ],
})
export class AddSkillsDropdownComponent {
  private readonly navDepth = signal(0);

  protected readonly overlay = viewChild.required(ConnectedOverlayDirective);
  protected readonly dropdown = viewChild(DropdownComponent);
  protected readonly chooseSkillTpl = viewChild.required<TemplateRef<unknown>>('chooseSkillTpl');
  protected readonly chooseMappingTpl =
    viewChild.required<TemplateRef<unknown>>('chooseMappingTpl');
  protected readonly proficiencyTpl = viewChild.required<TemplateRef<unknown>>('proficiencyTpl');
  protected readonly levelTpl = viewChild.required<TemplateRef<unknown>>('levelTpl');

  protected readonly skillSearch = signal('');
  protected readonly mappingSearch = signal('');
  protected readonly selectedSkill = signal<Skill | null>(null);
  protected readonly selectedMapping = signal<SkillMapping | null>(null);

  protected readonly dropdownWidth = computed<DropdownWidth>(() => {
    const widths: DropdownWidth[] = [
      DropdownWidth.Small,
      DropdownWidth.Medium,
      DropdownWidth.Large,
    ];
    return widths[this.navDepth()];
  });

  protected readonly filteredSkills = computed(() => {
    const query = this.skillSearch().trim().toLowerCase();
    if (!query) return this.skills();
    return this.skills().filter(s => s.name.toLowerCase().includes(query));
  });

  protected readonly filteredMappings = computed(() => {
    const query = this.mappingSearch().trim().toLowerCase();
    if (!query) return this.skillMappings();
    return this.skillMappings().filter(
      m =>
        m.name.toLowerCase().includes(query) ||
        m.skills.some(s => s.name.toLowerCase().includes(query)),
    );
  });
  protected readonly assignedSkillIdSet = computed(() => new Set(this.assignedSkillIds()));
  protected readonly assignedSkillMappingIdSet = computed(
    () => new Set(this.assignedSkillMappingIds()),
  );

  public readonly skills = input.required<Skill[]>();
  public readonly skillMappings = input.required<SkillMapping[]>();
  public readonly positionName = input.required<string>();
  public readonly loading = input(false);
  public readonly assignedSkillIds = input<number[]>([]);
  public readonly assignedSkillMappingIds = input<number[]>([]);

  public readonly skillSelected = output<{ skill: Skill; proficiency: Proficiency }>();
  public readonly skillMappingSelected = output<{
    skillMapping: SkillMapping;
    level: SkillLevel;
  }>();
  public readonly createNewSkillClicked = output<void>();
  public readonly manageSkillMappingsClicked = output<void>();

  protected openChooseSkill(event: Event): void {
    event.stopPropagation();
    this.navDepth.set(1);
    this.dropdown()!.setNestedTemplate(this.chooseSkillTpl());
  }

  protected openChooseMapping(event: Event): void {
    event.stopPropagation();
    this.navDepth.set(1);
    this.dropdown()!.setNestedTemplate(this.chooseMappingTpl());
  }

  protected isSkillAssigned(id: number): boolean {
    return this.assignedSkillIdSet().has(id);
  }

  protected isMappingAssigned(id: number): boolean {
    return this.assignedSkillMappingIdSet().has(id);
  }

  protected onSkillItemClick(skill: Skill, event: Event): void {
    if (!this.isSkillAssigned(skill.id)) {
      this.openProficiency(skill, event);
    }
  }

  protected onMappingItemClick(mapping: SkillMapping, event: Event): void {
    if (!this.isMappingAssigned(mapping.id)) {
      this.openLevel(mapping, event);
    }
  }

  protected openProficiency(skill: Skill, event: Event): void {
    event.stopPropagation();
    this.selectedSkill.set(skill);
    this.navDepth.set(2);
    this.dropdown()!.setNestedTemplate(this.proficiencyTpl());
  }

  protected openLevel(mapping: SkillMapping, event: Event): void {
    event.stopPropagation();
    this.selectedMapping.set(mapping);
    this.navDepth.set(2);
    this.dropdown()!.setNestedTemplate(this.levelTpl());
  }

  protected back(): void {
    this.navDepth.update(d => d - 1);
    this.dropdown()!.prevTemplate();
  }

  protected onSkillSelect(proficiency: Proficiency): void {
    this.skillSelected.emit({ skill: this.selectedSkill()!, proficiency });
    this.closeOverlay();
  }

  protected onCreateNewSkillClick(): void {
    this.createNewSkillClicked.emit();
    this.closeOverlay();
  }

  protected onManageSkillMappingsClick(): void {
    this.manageSkillMappingsClicked.emit();
    this.closeOverlay();
  }

  protected onMappingLevelSelect(level: SkillLevel): void {
    this.skillMappingSelected.emit({ skillMapping: this.selectedMapping()!, level });
    this.closeOverlay();
  }

  protected openOverlay(): void {
    this.overlay().open = true;
  }

  protected closeOverlay(): void {
    this.overlay().open = false;
    this.navDepth.set(0);
    this.skillSearch.set('');
    this.mappingSearch.set('');
    this.selectedSkill.set(null);
    this.selectedMapping.set(null);
    this.dropdown()?.clearTemplates();
  }

  protected resolveSkillsDescription(mapping: SkillMapping): string {
    return mapping.skills.map(s => s.name).join(', ');
  }
}
